import { FieldHelp } from "./FieldHelp";

export type Conviction = "high" | "mixed" | "single" | null;

const CONVICTION_META: Record<"high" | "mixed" | "single", { glyph: string; label: string; className: string }> = {
  high: { glyph: "◆◆◆", label: "High conviction", className: "conv-high" },
  mixed: { glyph: "◆◆◇", label: "Mixed conviction", className: "conv-mixed" },
  single: { glyph: "◆◇◇", label: "Single horizon", className: "conv-single" },
};

/** The full badge, for Instrument Detail's signal block. Pairs with
 * <ConvictionExplain> below it -- the badge alone doesn't explain itself,
 * and this feature only earns trust if the reasoning is visible, not just
 * the verdict (CLAUDE.md: "surface, don't hide"). */
export function ConvictionBadge({ conviction }: { conviction: Conviction }) {
  if (conviction === null) return null;
  const meta = CONVICTION_META[conviction];
  return (
    <span className={`chip conv-badge ${meta.className}`}>
      <span aria-hidden="true">{meta.glyph}</span> {meta.label}
      <FieldHelp term="cross_horizon_conviction" />
    </span>
  );
}

/** The compact chip, for list views (Stocks, Coverage resolver) where a
 * full badge + explanation would be too much per row. */
export function ConvictionChip({ conviction }: { conviction: Conviction }) {
  if (conviction === null) return null;
  const meta = CONVICTION_META[conviction];
  return (
    <span className={`chip conv-chip ${meta.className}`} title={meta.label}>
      <span aria-hidden="true">{meta.glyph}</span> {conviction === "high" ? "High" : conviction === "mixed" ? "Mixed" : "Single"}
    </span>
  );
}

/** The honest explanation box -- states the mechanism (sign agreement,
 * never magnitude, never blended into the score), not just the verdict.
 * `primaryHorizon`/`primarySignal` and `secondary` describe what actually
 * produced the verdict, so a reader can check the reasoning themselves. */
export function ConvictionExplain({
  conviction, primaryHorizon, primarySignal, secondary,
}: {
  conviction: Conviction;
  primaryHorizon: number | null;
  primarySignal: number | null;
  secondary: { horizon: number; signal: number }[];
}) {
  if (conviction === null || primaryHorizon === null || primarySignal === null) return null;
  const dir = (v: number) => (v >= 0 ? "positive" : "negative");
  let body: string;
  if (conviction === "single") {
    body = `Only the ${primaryHorizon}-day view has signal for this stock -- there is no other ` +
      "horizon to cross-check against, so no agreement/conflict verdict is possible. This is an " +
      "honest absence of a cross-check, not a low score.";
  } else if (conviction === "high") {
    const others = secondary.map((s) => `${s.horizon}-day (${dir(s.signal)})`).join(", ");
    body = `The ${primaryHorizon}-day view (${dir(primarySignal)}) and ${others} agree in direction. ` +
      "Each score stays horizon-specific and clean -- this reflects that the independently-computed " +
      "views happen to point the same way, not a combined or boosted magnitude.";
  } else {
    const others = secondary.map((s) => `${s.horizon}-day (${dir(s.signal)})`).join(", ");
    body = `The ${primaryHorizon}-day view (${dir(primarySignal)}) and ${others} do NOT agree in direction. ` +
      `Read the ${primaryHorizon}-day view as the near-term call; the conflict means lower confidence it sustains ` +
      "into the other horizon. Neither number is adjusted because of this -- both stay their own clean prediction.";
  }
  return (
    <div className={`conv-explain ${conviction === "mixed" ? "conv-explain-mixed" : conviction === "single" ? "conv-explain-single" : ""}`}>
      <b>{CONVICTION_META[conviction].label}</b> — {body}
    </div>
  );
}
